import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js'
import { authConfig, hasCognitoConfig } from './config'
import { clearSession, getSession, saveSession } from './storage'
import type { AuthSession, LoginInput, SignupInput } from './types'

function assertConfig() {
  if (!hasCognitoConfig()) {
    throw new Error('Cognito is not configured. Set NEXT_PUBLIC_NCP_COGNITO_* variables.')
  }
}

function getUserPool() {
  assertConfig()
  return new CognitoUserPool({
    UserPoolId: authConfig.userPoolId,
    ClientId: authConfig.userPoolClientId,
  })
}

function buildSession(user: CognitoUser, cognitoSession: any, email: string): AuthSession {
  return {
    email,
    userId: cognitoSession.getIdToken().payload.sub,
    tokens: {
      accessToken: cognitoSession.getAccessToken().getJwtToken(),
      idToken: cognitoSession.getIdToken().getJwtToken(),
      refreshToken: cognitoSession.getRefreshToken().getToken(),
      expiresAt: cognitoSession.getAccessToken().getExpiration() * 1000,
    },
  }
}

function normalizeEmail(email: string) {
  return email.trim().toLowerCase()
}

function assertEmail(email: string) {
  const normalized = normalizeEmail(email)
  if (!normalized) {
    throw new Error('Email is required.')
  }
  return normalized
}

export async function signUp(input: SignupInput) {
  const userPool = getUserPool()
  const email = assertEmail(input.email)
  const attributes = [new CognitoUserAttribute({ Name: 'name', Value: input.fullName })]

  return new Promise<void>((resolve, reject) => {
    userPool.signUp(email, input.password, attributes, [], (err) => {
      if (err) {
        reject(err)
        return
      }
      resolve()
    })
  })
}

export async function confirmSignUp(email: string, code: string) {
  const userPool = getUserPool()
  const normalizedEmail = assertEmail(email)
  const user = new CognitoUser({ Username: normalizedEmail, Pool: userPool })
  return new Promise<void>((resolve, reject) => {
    user.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err)
        return
      }
      resolve()
    })
  })
}

export async function login(input: LoginInput) {
  const userPool = getUserPool()
  const email = assertEmail(input.email)
  const user = new CognitoUser({ Username: email, Pool: userPool })
  const auth = new AuthenticationDetails({
    Username: email,
    Password: input.password,
  })

  return new Promise<AuthSession>((resolve, reject) => {
    user.authenticateUser(auth, {
      onSuccess: (session) => {
        const authSession = buildSession(user, session, email)
        saveSession(authSession)
        resolve(authSession)
      },
      onFailure: (err) => reject(err),
    })
  })
}

export async function requestPasswordReset(email: string) {
  const userPool = getUserPool()
  const normalizedEmail = assertEmail(email)
  const user = new CognitoUser({ Username: normalizedEmail, Pool: userPool })
  return new Promise<void>((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })
}

export async function confirmPasswordReset(email: string, code: string, newPassword: string) {
  const userPool = getUserPool()
  const normalizedEmail = assertEmail(email)
  const user = new CognitoUser({ Username: normalizedEmail, Pool: userPool })
  return new Promise<void>((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })
}

export async function refreshAuthSession() {
  const userPool = getUserPool()
  const existing = getSession()
  if (!existing) {
    throw new Error('No active session found.')
  }

  const user = new CognitoUser({ Username: existing.email, Pool: userPool })
  const refreshToken = new CognitoRefreshToken({ RefreshToken: existing.tokens.refreshToken })
  return new Promise<AuthSession>((resolve, reject) => {
    user.refreshSession(refreshToken, (err, session) => {
      if (err || !session) {
        clearSession()
        reject(err || new Error('Failed to refresh session.'))
        return
      }
      const authSession = buildSession(user, session, existing.email)
      saveSession(authSession)
      resolve(authSession)
    })
  })
}

export function signOut() {
  const existing = getSession()
  if (existing && hasCognitoConfig()) {
    const userPool = getUserPool()
    const user = new CognitoUser({ Username: existing.email, Pool: userPool })
    user.signOut()
  }
  clearSession()
}

export function getValidSession() {
  return getSession()
}
